/**
 * GET /api_ats/v1/ats_interviews/interviewers
 *
 * Returns the list of eligible interviewers with the weekly workload
 * ("X / Y hrs") we surface in the scheduling async-select. Same
 * endpoint the legacy KInterviewScheduler populates its dropdown
 * with. The response splits pool interviewers from the rest so the
 * UI can group them.
 *
 * Jbuilder shape (see
 * app/views/api_ats/v1/ats_interviews/_interviewer.json.jbuilder):
 *   {
 *     id, name, avatar, time_zone,
 *     week_interview_hours, week_interview_hours_limit
 *   }
 */
import { api } from '@/api/request';

export interface ListedInterviewer {
  id: number;
  name: string;
  avatar?: string;
  time_zone?: string;
  week_interview_hours?: number;
  week_interview_hours_limit?: number | null;
  can_host_zoom_meeting?: boolean;
}

export interface FetchInterviewersListArgs {
  /** Free-text search (maps to backend `q`). Empty string returns the pool first, then the rest paginated. */
  query?: string;
  /**
   * Sunday of the visible week in YYYY-MM-DD. Backend computes
   * `week_interview_hours` per attendee against this anchor.
   */
  weekStartDate: string;
  /** Used to determine pool priority. Omit for adhoc. */
  atsJobStageActionId?: number;
  signal?: AbortSignal;
}

interface RawResponse {
  pool_interviewers?: ListedInterviewer[];
  interviewers?: ListedInterviewer[];
}

export interface InterviewersListResult {
  pool: ListedInterviewer[];
  other: ListedInterviewer[];
}

export async function fetchInterviewersList(
  args: FetchInterviewersListArgs
): Promise<InterviewersListResult> {
  const params: Record<string, unknown> = {
    week_start_date: args.weekStartDate,
  };
  if (args.query && args.query.trim().length > 0) {
    params.q = args.query.trim();
  }
  if (
    typeof args.atsJobStageActionId === 'number' &&
    args.atsJobStageActionId > 0
  ) {
    params.ats_job_stage_action_id = args.atsJobStageActionId;
  }
  const body = await api.get<RawResponse>('ats_interviews/interviewers', {
    params,
    signal: args.signal,
    // Jbuilder emits the fields at the root — runtime envelope unwrap
    // handles the {data: ...} wrapping when present.
  });
  return {
    pool: Array.isArray(body.pool_interviewers) ? body.pool_interviewers : [],
    other: Array.isArray(body.interviewers) ? body.interviewers : [],
  };
}
